import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { setToogleHeader } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import {
  Table,
  DatePicker,
  Select,
  message,
  Card,
  Tabs,
  InputNumber,
} from "antd";
import { fetchBrandsBySelectedClients } from "../../core/api/settings";
import { useMemo } from "react";
import { processListUsage } from "../../core/api/report";
import dayjs from "dayjs";
import { getCurrentUser } from "../../core/api/settings";
import { getProfileTest } from "../../core/api/auth";


const ListReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const data = useSelector((state) => state.toggle_header);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [brands, setBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [listData, setListData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [minMax, setMinMax] = useState({ min: 0, max: 0 });
  // Check if we're embedded in MailingResponseTabs by looking at the parent route
  const isEmbedded = location.pathname.includes('/list-report') && !location.pathname.includes('/list-report/');
  
  // Get the path segment for tab selection
  const pathSegment = location.pathname.split("/").pop();
  
  // If embedded, don't use URL for tab state
  const [internalActiveTab, setInternalActiveTab] = useState("main");
  
  // Determine active tab - if embedded use internal state, otherwise use URL
  const activeTab = isEmbedded 
    ? internalActiveTab 
    : (["main", "top-lists-per-product", "top-lists-by-category"].includes(pathSegment) 
        ? pathSegment 
        : "main");
  const [startDate, setStartDate] = useState(dayjs("2023-01-01"));
  const [endDate, setEndDate] = useState(dayjs().subtract(100, "day"));
  const [topDays, setTopDays] = useState(10);
  const [performanceField, setPerformanceField] = useState("net_profit_loss");
  const colorFromPerformance = (p, min = 0, max = 0) => {
    const mid = 0;
    min = Math.max(min, -1000);
    max = Math.min(max, 500);

    if (p < mid) {
      const ratio = (p - min) / (mid - min);
      const r = 255;
      const g = Math.round(255 * ratio);
      const b = Math.round(255 * ratio);
      return `rgb(${r},${g},${b})`;
    } else {
      const ratio = (p - mid) / (max - mid);
      const r = Math.round(255 * (1 - ratio));
      const g = 255;
      const b = Math.round(255 * (1 - ratio));
      return `rgb(${r},${g},${b})`;
    }
  };

  const columns = useMemo(() => {
    if (!listData.length) return [];

    const keys = Object.keys(listData[0]).filter((key) => key !== "children");

    return keys.map((key) => {
      if (key === "List Name" || key === "Total Usage" || key === "Mailed") {
        return {
          title: key,
          dataIndex: key,
          key,
          fixed: key === "List Name" ? "left" : "",
          minWidth: 100,
        };
      } else {
        return {
          title: key,
          dataIndex: key,
          key,
          render: (cell) => {
            if (!cell) return { children: "" };

            return {
              props: {
                style: {
                  background: colorFromPerformance(
                    cell.performance,
                    minMax.min,
                    minMax.max
                  ),
                  textAlign: "center",
                },
              },
              children:
                cell.value === 0 ? (
                  ""
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <div>
                          <div>
                            Average {performanceField}: $
                            {parseFloat(cell.performance).toFixed(2)}
                          </div>
                          <strong>Formula:</strong>
                          <div>
                            Average {performanceField} = Total{" "}
                            {performanceField} for (List, Product) / Number of
                            Times (List, Product) Appeared.
                          </div>
                          <div>List: {cell.listName}</div>
                          {cell.segment && <div>Segment: {cell.segment}</div>}
                          <div>Product: {cell.productName}</div>
                        </div>
                      </Tooltip>
                    }
                  >
                    <span style={{ padding: "30px" }}>{cell.value}</span>
                  </OverlayTrigger>
                ),
            };
          },
        };
      }
    });
  }, [listData, minMax]);

  const topListsByCategoryData = useMemo(() => {
    if (!categoryData.length) return { columns: [], dataSource: [] };

    const categoryRows = [];
    
    categoryData.forEach((category) => {
      const sortedLists = [...(category.Lists || [])]
        .sort((a, b) => {
          if (!a.performance && !b.performance) return 0;
          if (!a.performance) return 1;
          if (!b.performance) return -1;
          return b.performance - a.performance;
        })
        .slice(0, topDays);

      const row = {
        key: category.Category,
        categoryName: category.Category,
      };

      sortedLists.forEach((list, index) => {
        row[`topList${index + 1}`] = {
          listName: list.listName,
          performance: list.performance,
          value: list.value,
          category: list.category,
          segments: list.segments || []
        };
      });

      categoryRows.push(row);
    });

    const topListColumns = [
      {
        title: "Category",
        dataIndex: "categoryName",
        key: "categoryName",
        fixed: "left",
        width: 200,
      },
    ];

    for (let i = 1; i <= topDays; i++) {
      // Alternate background colors for columns
      const bgColor = i % 2 === 1 ? "#f0f7ff" : "#f5fff0";

      topListColumns.push({
        title: (
          <div
            style={{
              backgroundColor: bgColor,
              padding: "8px 0",
              borderRadius: "4px",
            }}
          >
            Top {i} List
          </div>
        ),
        key: `topList${i}`,
        children: [
          {
            title: "List Name",
            dataIndex: [`topList${i}`, "listName"],
            key: `topList${i}Name`,
            width: 200,
            render: (text, record) => {
              if (!text || !record[`topList${i}`]?.performance) return "";
              
              const segments = record[`topList${i}`]?.segments || [];
              
              if (segments.length === 0) return text;
              
              return (
                <div>
                  <div>{text}</div>
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="auto"
                    container={document.body}
                    popperConfig={{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: document.body,
                          },
                        },
                      ],
                    }}
                    overlay={
                      <div
                        id={`segments-overlay-category-${record.key}-${i}`}
                        className="custom-segments-overlay"
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #d9d9d9",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          padding: "16px",
                          maxHeight: "600px",
                          overflowY: "auto",
                          zIndex: 1050,
                        }}
                      >
                        <div style={{ marginBottom: "12px" }}>
                          <h6
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              paddingBottom: "8px",
                              marginBottom: "12px",
                              fontWeight: "600",
                              color: "#1890ff",
                            }}
                          >
                            Top Segments for {text} in {record.categoryName}
                          </h6>
                          <table
                            className="table table-sm table-bordered"
                            style={{ marginBottom: 0 }}
                          >
                            <thead style={{ backgroundColor: "#f5f5f5" }}>
                              <tr>
                                <th style={{ padding: "8px 12px" }}>Segment</th>
                                <th
                                  style={{
                                    padding: "8px 12px",
                                    width: "120px",
                                  }}
                                >
                                  Avg. {performanceField}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {segments.map((segment, idx) => (
                                <tr key={idx}>
                                  <td style={{ padding: "8px 12px" }}>
                                    {segment.segment}
                                  </td>
                                  <td
                                    style={{
                                      background: colorFromPerformance(
                                        segment.performance,
                                        minMax.min,
                                        minMax.max
                                      ),
                                      textAlign: "center",
                                      padding: "8px 12px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    $
                                    {parseFloat(segment.performance).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    }
                  >
                    <button
                      className="btn btn-xs btn-outline-info"
                      style={{
                        marginTop: "4px",
                        padding: "0px 4px",
                        fontSize: "10px",
                      }}
                    >
                      View Segments
                    </button>
                  </OverlayTrigger>
                </div>
              );
            },
          },
          {
            title: (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <div>
                      <strong>Formula:</strong>
                      <div>
                        Average {performanceField} = Total {performanceField}{" "}
                        for (List, Category) / Number of Times (List, Category)
                        Appeared.
                      </div>
                    </div>
                  </Tooltip>
                }
              >
                <span>Average {performanceField}</span>
              </OverlayTrigger>
            ),
            dataIndex: [`topList${i}`, "performance"],
            key: `topList${i}Performance`,
            width: 120,
            render: (performance, record) => {
              if (!performance) return "";

              return {
                props: {
                  style: {
                    background: colorFromPerformance(
                      performance,
                      minMax.min,
                      minMax.max
                    ),
                    textAlign: "center",
                    padding: "8px",
                  },
                },
                children: (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <div>
                          <strong>Formula:</strong>
                          <div>
                            Average {performanceField} = Total{" "}
                            {performanceField} for (List, Category) / Number of
                            Times (List, Category) Appeared.
                          </div>
                          <div>List: {record[`topList${i}`]?.listName}</div>
                          <div>Category: {record.categoryName}</div>
                        </div>
                      </Tooltip>
                    }
                  >
                    <span>${parseFloat(performance).toFixed(2)}</span>
                  </OverlayTrigger>
                ),
              };
            },
          },
        ],
      });
    }

    return { columns: topListColumns, dataSource: categoryRows };
  }, [categoryData, minMax, topDays, performanceField]);

  const topListsPerProductData = useMemo(() => {
    if (!listData.length) return { columns: [], dataSource: [] };

    const products = new Set();
    const productToListPerformance = new Map();

    listData.forEach((list) => {
      const listName = list["List Name"];

      if (!listName) return;

      Object.keys(list).forEach((key) => {
        if (
          key === "List Name" ||
          key === "Total Usage" ||
          key === "Mailed" ||
          key === "children" ||
          !list[key]
        ) {
          return;
        }

        const productName = key;
        products.add(productName);

        if (!productToListPerformance.has(productName)) {
          productToListPerformance.set(productName, []);
        }

        productToListPerformance.get(productName).push({
          listName: listName,
          performance: list[key].performance,
          value: list[key].value,
          segments: [],
        });
      });
    });

    listData.forEach((list) => {
      const listName = list["List Name"];
      if (!list.children || !listName) return;

      list.children.forEach((segment) => {
        const segmentName = segment["List Name"].replace("Segment: ", "");

        Object.keys(segment).forEach((key) => {
          if (
            key === "List Name" ||
            key === "Total Usage" ||
            key === "Mailed" ||
            !segment[key]
          ) {
            return;
          }

          const productName = key;

          if (productToListPerformance.has(productName)) {
            const productLists = productToListPerformance.get(productName);
            const listEntry = productLists.find((l) => l.listName === listName);

            if (listEntry) {
              listEntry.segments.push({
                segmentName: segmentName,
                performance: segment[key].performance,
                value: segment[key].value,
              });
            }
          }
        });
      });
    });

    const productRows = [];
    const sortedProducts = Array.from(products).sort();

    sortedProducts.forEach((productName) => {
      const lists = productToListPerformance.get(productName) || [];

      const topLists = [...lists]
        .sort((a, b) => {
          if (!a.performance && !b.performance) return 0;

          if (!a.performance) return 1;

          if (!b.performance) return -1;

          return b.performance - a.performance;
        })
        .slice(0, topDays);

      const row = {
        key: productName,
        productName: productName,
      };

      topLists.forEach((list, index) => {
        const sortedSegments = [...list.segments]
          .sort((a, b) => {
            if (!a.performance && !b.performance) return 0;

            if (!a.performance) return 1;

            if (!b.performance) return -1;

            return b.performance - a.performance;
          })
          .slice(0, 5);

        row[`topList${index + 1}`] = {
          listName: list.listName,
          performance: list.performance,
          value: list.value,
          segments: sortedSegments,
        };
      });

      productRows.push(row);
    });

    const topListColumns = [
      {
        title: "Product",
        dataIndex: "productName",
        key: "productName",
        fixed: "left",
        width: 200,
      },
    ];

    for (let i = 1; i <= topDays; i++) {
      // Alternate background colors for columns
      const bgColor = i % 2 === 1 ? "#f0f7ff" : "#f5fff0";

      topListColumns.push({
        title: (
          <div
            style={{
              backgroundColor: bgColor,
              padding: "8px 0",
              borderRadius: "4px",
            }}
          >
            Top {i} List
          </div>
        ),
        key: `topList${i}`,
        children: [
          {
            title: "List Name",
            dataIndex: [`topList${i}`, "listName"],
            key: `topList${i}Name`,
            width: 200,
            render: (text, record) => {
              if (!text || !record[`topList${i}`]?.performance) return "";

              const segments = record[`topList${i}`]?.segments || [];

              if (segments.length === 0) return text;

              return (
                <div>
                  <div>{text}</div>
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="auto"
                    container={document.body}
                    popperConfig={{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: document.body,
                          },
                        },
                      ],
                    }}
                    overlay={
                      <div
                        id={`segments-overlay-${record.key}-${i}`}
                        className="custom-segments-overlay"
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #d9d9d9",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          padding: "16px",
                          maxHeight: "600px",
                          overflowY: "auto",
                          zIndex: 1050,
                        }}
                      >
                        <div style={{ marginBottom: "12px" }}>
                          <h6
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              paddingBottom: "8px",
                              marginBottom: "12px",
                              fontWeight: "600",
                              color: "#1890ff",
                            }}
                          >
                            Top Segments for {record.productName} in {text}
                          </h6>
                          <table
                            className="table table-sm table-bordered"
                            style={{ marginBottom: 0 }}
                          >
                            <thead style={{ backgroundColor: "#f5f5f5" }}>
                              <tr>
                                <th style={{ padding: "8px 12px" }}>Segment</th>
                                <th
                                  style={{
                                    padding: "8px 12px",
                                    width: "120px",
                                  }}
                                >
                                  Avg. {performanceField}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {segments.map((segment, idx) => (
                                <tr key={idx}>
                                  <td style={{ padding: "8px 12px" }}>
                                    {segment.segmentName}
                                  </td>
                                  <td
                                    style={{
                                      background: colorFromPerformance(
                                        segment.performance,
                                        minMax.min,
                                        minMax.max
                                      ),
                                      textAlign: "center",
                                      padding: "8px 12px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    $
                                    {parseFloat(segment.performance).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    }
                  >
                    <button
                      className="btn btn-xs btn-outline-info"
                      style={{
                        marginTop: "4px",
                        padding: "0px 4px",
                        fontSize: "10px",
                      }}
                    >
                      View Segments
                    </button>
                  </OverlayTrigger>
                </div>
              );
            },
          },
          {
            title: (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <div>
                      <strong>Formula:</strong>
                      <div>
                        Average {performanceField} = Total {performanceField}{" "}
                        for (List, Product) / Number of Times (List, Product)
                        Appeared.
                      </div>
                    </div>
                  </Tooltip>
                }
              >
                <span>Average {performanceField}</span>
              </OverlayTrigger>
            ),
            dataIndex: [`topList${i}`, "performance"],
            key: `topList${i}Performance`,
            width: 120,
            render: (performance, record) => {
              if (!performance) return "";

              return {
                props: {
                  style: {
                    background: colorFromPerformance(
                      performance,
                      minMax.min,
                      minMax.max
                    ),
                    textAlign: "center",
                    padding: "8px",
                  },
                },
                children: (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <div>
                          <strong>Formula:</strong>
                          <div>
                            Average {performanceField} = Total{" "}
                            {performanceField} for (List, Product) / Number of
                            Times (List, Product) Appeared.
                          </div>
                          <div>List: {record[`topList${i}`]?.listName}</div>
                          <div>Product: {record.productName}</div>
                        </div>
                      </Tooltip>
                    }
                  >
                    <span>${parseFloat(performance).toFixed(2)}</span>
                  </OverlayTrigger>
                ),
              };
            },
          },
        ],
      });
    }

    return { columns: topListColumns, dataSource: productRows };
  }, [listData, minMax, topDays]);

  useEffect(() => {
    setBrands([]);
    setSelectedBrands(null);
    fetchBrandsBySelectedClients(selectedClients)
      .then((res) => {
        if (!res.data || res.data.length === 0) return;
        setBrands(res.data);
        if (res.data.length === 1) {
          const autoSelectedBrand = res.data[0].id;
          setSelectedBrands([autoSelectedBrand]);
        }
        setLoadingBrands(false);
      })
      .catch(() => {
        setLoadingBrands(false);
        message.error("Failed to fetch brands.");
      });
  }, [selectedClients]);

  const fetchData = useCallback(async () => {
    try {
      const response = await processListUsage(
        selectedClients,
        selectedBrands,
        startDate,
        endDate,
        performanceField
      );

      const {
        data: lists,
        category_data: categories,
        min_profit_per_buyer,
        max_profit_per_buyer,
      } = response.data;
      const transformedData = lists.map((listItem) => {
        const { Segments = [], ...listRow } = listItem;

        const children = Segments.map((segment) => {
          const segmentRow = {
            "List Name": `Segment: ${segment.Segment}`,
            "Total Usage": segment.Products.reduce(
              (acc, p) => acc + p.value,
              0
            ),
            Mailed: segment.Mailed,
          };

          segment.Products.forEach((prod) => {
            segmentRow[prod.productName] = {
              value: prod?.value,
              performance: prod?.performance,
              listName: prod?.listName,
              productName: prod?.productName,
              segment: segment.Segment,
            };
          });

          return segmentRow;
        });

        return {
          ...listRow,
          children: children.length ? children : undefined,
        };
      });

      setListData(transformedData);
      setCategoryData(categories || []);
      setMinMax({ min: min_profit_per_buyer, max: max_profit_per_buyer });
    } catch (error) {
      console.error("Error fetching list usage data:", error);
    }
  }, [selectedClients, selectedBrands, startDate, endDate, performanceField]);

  useEffect(() => {
    fetchData();
  }, [
    selectedBrands,
    selectedClients,
    startDate,
    endDate,
    fetchData,
    performanceField,
  ]);

    
        // Permission check
    
  

  const tabsItems = [
    {
      label: "List Report",
      key: "main",
      children: (
        <Table
          columns={columns}
          dataSource={listData}
          rowKey="List Name"
          className="table-sm small-font"
          pagination={false}
          bordered
          scroll={{ x: "max-content", y: "max-content" }}
          size="small"
          expandable={{
            expandIconColumnIndex: columns.findIndex(
              (col) => col.dataIndex === "List Name"
            ),
          }}
        />
      ),
    },
    {
      label: "Top Lists per Product",
      key: "top-lists-per-product",
      children: (
        <div className="position-relative">
          <Table
            columns={topListsPerProductData.columns}
            dataSource={topListsPerProductData.dataSource}
            rowKey="productName"
            className="table-sm small-font"
            pagination={false}
            bordered
            scroll={{ x: "max-content", y: 500 }}
            size="small"
            sticky
          />
        </div>
      ),
    },
    {
      label: "Top Lists by Category",
      key: "top-lists-by-category",
      children: (
        <div className="position-relative">
          <Table
            columns={topListsByCategoryData.columns}
            dataSource={topListsByCategoryData.dataSource}
            rowKey="categoryName"
            className="table-sm small-font"
            pagination={false}
            bordered
            scroll={{ x: "max-content", y: 500 }}
            size="small"
            sticky
          />
        </div>
      ),
    },
  ];


  return (
    <div className="">
      <div className="">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>List Report</h4>
            </div>
          </div>
          <ul className="table-top-head">
            {(activeTab === "top-lists-per-product" || activeTab === "top-lists-by-category") && (
              <li className="d-flex">
                <label htmlFor="top-days" className="mt-2 me-1">
                  Top Lists:
                </label>
                <InputNumber
                  id="top-days"
                  style={{ width: 80 }}
                  value={topDays}
                  min={1}
                  max={100}
                  onChange={(value) => setTopDays(value)}
                />
              </li>
            )}
            <li className="d-flex">
              <label htmlFor="start-date" className="mt-2 me-1">
                Start
              </label>
              <DatePicker
                placeholder="Start Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
              />
            </li>
            <li className="d-flex">
              <label htmlFor="end-date" className="mt-2 me-1">
                End
              </label>
              <DatePicker
                placeholder="End Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
              />
            </li>
            <li>
              <Select
                placeholder="Select Brand"
                options={brands.map((brand) => ({
                  value: brand.id,
                  label: brand.brand_name,
                }))}
                onChange={(value) => setSelectedBrands(value)}
                value={selectedBrands}
                loading={loadingBrands}
                mode="multiple"
              />
            </li>
            <li className="d-flex">
              <label htmlFor="performance-field" className="mt-2 me-1">
                Performance
              </label>
              <Select
                id="performance-field"
                placeholder="Select Performance Field"
                options={[
                  { value: "net_profit_loss", label: "Profit" },
                  {
                    value: "net_profit_loss_per_mail",
                    label: "Net Profit/Loss Per Mail",
                  },
                  {
                    value: "profit_loss_per_buyer",
                    label: "Profit/Loss Per Buyer",
                  },
                ]}
                onChange={(value) => setPerformanceField(value)}
                value={performanceField}
                style={{ minWidth: "200px" }}
              />
            </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Excel</Tooltip>}
              >
                <Link
                  onClick={() => {
                    // intentionally empty
                  }}
                >
                  <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Refresh</Tooltip>}
              >
                <Link onClick={fetchData}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Collapse</Tooltip>}
              >
                <Link
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
        <div className="card">
          <Card size="small" className="table-responsive">
            <Tabs
              activeKey={activeTab}
              items={tabsItems}
              onChange={(key) => {
                if (isEmbedded) {
                  // If embedded, just update internal state without navigation
                  setInternalActiveTab(key);
                } else {
                  // Otherwise navigate as usual
                  navigate(`/list-report/${key}`);
                }
              }}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ListReport;
